// Full notifications page (routed at /{role}/notifications). All / Unread
// filter, paginated list with "Load more", inline-styled with EM Hub tokens.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Plane, Clock, AlarmClock, CalendarClock, CheckCheck, Inbox } from 'lucide-react';
import { T } from '../../components/em/tokens';
import { Page, PageHeader } from '../../components/em/ui';
import { notificationsApi, resolveNotifLink, notifMeta } from './notificationsApi';
import { relTime } from './NotificationBell';

const PAGE_SIZE = 30;
const ICONS = {
  plane: Plane,
  clock: Clock,
  'alarm-clock': AlarmClock,
  'calendar-clock': CalendarClock,
};
const iconFor = (type) => ICONS[notifMeta(type).icon] || Bell;

function NotifCard({ n, onClick }) {
  const Icon = iconFor(n.type);
  const tone = notifMeta(n.type).tone;
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      onClick={() => onClick(n)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex', gap: 14, alignItems: 'flex-start', width: '100%', textAlign: 'left',
        cursor: 'pointer', padding: '16px 18px', marginBottom: 10,
        border: `1px solid ${n.read ? T.border : T.orangeEdge}`, borderRadius: T.rLg,
        background: n.read ? (hover ? T.bgMuted : T.bgCard) : (hover ? 'rgba(245,75,1,0.10)' : T.orange50),
        boxShadow: T.shadowSm, transition: 'background .12s, border-color .12s',
      }}
    >
      <div style={{
        width: 42, height: 42, borderRadius: T.pill, flexShrink: 0,
        background: `${tone}1a`, color: tone,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon size={20} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ flex: 1, minWidth: 0, font: `600 15px/1.3 ${T.font}`, color: T.fg1 }}>{n.title}</span>
          {!n.read && <span style={{ width: 8, height: 8, borderRadius: T.pill, background: T.orange, flexShrink: 0 }} />}
          <span style={{ font: `400 12px/1 ${T.font}`, color: T.fg7, flexShrink: 0 }}>{relTime(n.created_at)}</span>
        </div>
        {n.body && (
          <div style={{ marginTop: 4, font: `400 13px/1.55 ${T.font}`, color: T.fg4 }}>{n.body}</div>
        )}
      </div>
    </button>
  );
}

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all'); // 'all' | 'unread'
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const load = (f) => {
    setLoading(true);
    notificationsApi.list({ limit: PAGE_SIZE, unread: f === 'unread' ? 1 : undefined })
      .then((r) => {
        const list = r.notifications || [];
        setItems(list);
        setHasMore(list.length >= PAGE_SIZE);
      })
      .catch(() => { setItems([]); setHasMore(false); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(filter); }, [filter]);

  const loadMore = () => {
    const last = items[items.length - 1];
    if (!last) return;
    setLoadingMore(true);
    notificationsApi.list({ limit: PAGE_SIZE, before: last.id, unread: filter === 'unread' ? 1 : undefined })
      .then((r) => {
        const list = r.notifications || [];
        setItems((prev) => [...prev, ...list]);
        setHasMore(list.length >= PAGE_SIZE);
      })
      .catch(() => setHasMore(false))
      .finally(() => setLoadingMore(false));
  };

  const handleMarkAll = () => {
    setItems((list) => (filter === 'unread' ? [] : list.map((n) => ({ ...n, read: true }))));
    notificationsApi.markAllRead().catch(() => {});
  };

  const handleClick = (n) => {
    if (!n.read) {
      setItems((list) => (filter === 'unread'
        ? list.filter((x) => x.id !== n.id)
        : list.map((x) => (x.id === n.id ? { ...x, read: true } : x))));
      notificationsApi.markRead(n.id).catch(() => {});
    }
    const to = resolveNotifLink(n.link);
    if (to) navigate(to);
  };

  const pill = (key, label) => {
    const on = filter === key;
    return (
      <button
        type="button"
        onClick={() => setFilter(key)}
        style={{
          padding: '7px 16px', borderRadius: T.pill, border: `1px solid ${on ? T.orange : T.border}`,
          background: on ? T.orange50 : '#fff', color: on ? T.orange : T.fg5,
          font: `${on ? 600 : 500} 13px/1 ${T.font}`, cursor: 'pointer',
        }}
      >{label}</button>
    );
  };

  const markAllBtn = (
    <button
      type="button"
      onClick={handleMarkAll}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 14px',
        borderRadius: T.rMd, border: `1px solid ${T.borderSoft}`, background: 'var(--em-bg-card)',
        color: T.fg1, font: `500 13px/1 ${T.font}`, cursor: 'pointer',
      }}
    >
      <CheckCheck size={15} /> Mark all read
    </button>
  );

  return (
    <Page>
      <PageHeader icon={Bell} title="Notifications" subtitle="Your alerts and updates" actions={markAllBtn} />

      <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
        {pill('all', 'All')}
        {pill('unread', 'Unread')}
      </div>

      {loading ? (
        <div style={{ padding: '56px 24px', textAlign: 'center', font: `400 14px ${T.font}`, color: T.fg5 }}>Loading…</div>
      ) : items.length === 0 ? (
        <div style={{ padding: '64px 24px', textAlign: 'center' }}>
          <div style={{
            width: 64, height: 64, borderRadius: T.pill, background: T.bgMuted,
            display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px',
          }}>
            <Inbox size={28} color={T.fg7} />
          </div>
          <div style={{ font: `600 16px ${T.font}`, color: T.fg1, marginBottom: 6 }}>
            {filter === 'unread' ? 'No unread notifications' : 'No notifications yet'}
          </div>
          <div style={{ font: `400 13px ${T.font}`, color: T.fg5 }}>You're all caught up.</div>
        </div>
      ) : (
        <>
          {items.map((n) => <NotifCard key={n.id} n={n} onClick={handleClick} />)}
          {hasMore && (
            <div style={{ textAlign: 'center', marginTop: 8 }}>
              <button
                type="button"
                onClick={loadMore}
                disabled={loadingMore}
                style={{
                  padding: '10px 20px', borderRadius: T.rMd, border: `1px solid ${T.border}`,
                  background: 'var(--em-bg-card)', color: T.fg3, font: `500 13px/1 ${T.font}`,
                  cursor: loadingMore ? 'default' : 'pointer',
                }}
              >{loadingMore ? 'Loading…' : 'Load more'}</button>
            </div>
          )}
        </>
      )}
    </Page>
  );
}
